'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { formatPercentage } from '@/lib/formatters/percentage.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Badge } from '@/ui/badge/Badge'
import { useAtomValue } from 'jotai'
import type React from 'react'
import { selectedReferenceAtom } from '../../services/product.store'
import type { ProductTierPriceProps } from '../product-tiers-prices/ProductTiersPrices.client'

export type Discount = {
  /** The discounted price */
  price: number
  /** The discounted percentage */
  percentage: number
  /** The price tiers */
  tiersPrices?: ProductTierPriceProps[]
}

type ProductPriceHeaderProps = {
  keepHeadSpace: boolean
  isProduct: boolean
  hasDiscount: boolean
  price: number
  discount?: Discount
  showPercentageTag: boolean
}

export function ProductPriceHeader({
  keepHeadSpace,
  isProduct,
  hasDiscount,
  price,
  discount,
  showPercentageTag,
}: ProductPriceHeaderProps): React.ReactNode {
  const { t } = useTranslation(['productPrice'])
  const productPriceHeaderClasses = twMerge('mb-1 flex flex-row', hasDiscount && 'mb-0.5')
  const selectedReference = useAtomValue(selectedReferenceAtom)

  if (!keepHeadSpace && !isProduct && !hasDiscount) return null

  const oldPrice = hasDiscount && discount ? price + discount.price : undefined

  if (isProduct && !selectedReference) {
    return (
      <div className={productPriceHeaderClasses} data-testid="product-price/price-from">
        <span className="text-sm uppercase text-brand-2-700">{t('productPrice:priceFrom')}</span>
      </div>
    )
  }

  if (hasDiscount && discount && oldPrice) {
    return (
      <div
        className={twMerge(productPriceHeaderClasses, 'flex items-center gap-1')}
        data-testid="product-price/price-discounted"
      >
        <p className="text-brand-2-600 line-through">{formatPrice(oldPrice)}</p>
        {showPercentageTag && typeof discount.percentage === 'number' && (
          <Badge tint="discount" size="sm">
            {formatPercentage(discount.percentage, '-')}
          </Badge>
        )}
      </div>
    )
  }

  return null
}
